import Button from "@ui/button/Button";
import { Error } from "@ui/error/Error";
import Spacer from "@ui/spacer/Spacer";
import { useEffect, useState } from "react";
import { GD } from "src/GD";
import { clearAuthStorage } from "src/utils/authStorage";
import { FormParser } from "src/view/FormParser";
import { StartScreenView } from "../StartScreenView";
import {
	AUTH_FORM_ACTIONS_SX,
	AUTH_PRIMARY_BUTTON_SX,
	AUTH_SECONDARY_BUTTON_SX,
	AuthCard,
	AuthDivider,
	AuthStatus,
} from "./AuthLayout";

const form = {
	password: "",
	$__password_type: "password",
	$__password_autoComplete: "new-password",
	$__password_label: "New password",
	confirm_password: "",
	$__confirm_password_type: "password",
	$__confirm_password_autoComplete: "new-password",
	$__confirm_password_label: "Confirm new password",
};

type TNewPassForm = typeof form;

export const RestorePassword = ({ code }: { code: string }) => {
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<IError>();
	const [completed, setCompleted] = useState<boolean>();
	const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
	const [tokenVerified, setTokenVerified] = useState(false);

	const getForm = () => ({ ...form });

	const validate = (data: TNewPassForm): boolean => {
		if (!data.password) {
			setError({ code: 0, message: "Empty password" });
			return false;
		}
		if (data.password !== data.confirm_password) {
			setError({ code: 0, message: "Your passwords don't match. Please check and try again." });
			return false;
		}
		if (data.password.length < 5) {
			setError({ code: 0, message: "Password must be at least 5 characters long." });
			return false;
		}
		return true;
	};

	useEffect(() => {
		const verifyToken = async () => {
			if (!code) {
				setError({ code: 0, message: "Reset token is missing." });
				return;
			}

			setBusy(true);
			const response = await GD.REQ_API.request({
				method: "users/password/verify",
				data: { token: code },
			});
			setBusy(false);

			if (response.error) {
				setError(response.error);
				return;
			}

			const result = (response.data as any)?.data ?? response.data;
			if (result?.status === true) {
				setVerifiedEmail(result.email ?? null);
				setTokenVerified(true);
				return;
			}

			setError({ code: 0, message: "Reset token is invalid or expired." });
		};

		void verifyToken();
	}, [code]);

	const checking = busy && !tokenVerified && !error;

	return (
		<StartScreenView busy={busy}>
			<AuthCard
				icon={completed ? "success" : error && !tokenVerified ? "error" : checking ? "loading" : "shield"}
				eyebrow="Password recovery"
				title={completed ? "Password updated" : error && !tokenVerified ? "Reset link unavailable" : "Choose a new password"}
				subtitle={
					completed
						? "Your new password is active and your previous authentication data has been cleared."
						: "We verify the reset link before accepting a new password."
				}>
				{checking ? <AuthStatus title="Checking reset link">Please wait while we validate this request.</AuthStatus> : null}

				{completed ? (
					<AuthStatus tone="success" title="Password changed successfully">
						You can now sign in with your new password.
					</AuthStatus>
				) : null}

				{error ? <div role="alert"><Error error={error} sx={{ marginBottom: tokenVerified ? "18px" : 0 }} /></div> : null}

				{!completed && tokenVerified ? (
					<>
						{verifiedEmail ? (
							<>
								<AuthStatus title="Verified account">Resetting the password for {verifiedEmail}.</AuthStatus>
								<Spacer size="18px" />
							</>
						) : null}
						<FormParser
							key="restoreForm"
							ignoreObserver={true}
							fullSizeButtons={true}
							actionsSx={AUTH_FORM_ACTIONS_SX}
							secondaryButton={null}
							primaryButton={{ title: "Update password", variant: "primary", sx: { ...AUTH_PRIMARY_BUTTON_SX, flexGrow: 1 } }}
							onSubmit={async (data: TNewPassForm): Promise<any> => {
								setError(undefined);
								if (validate(data)) {
									setBusy(true);
									const response = await GD.REQ_API.request({
										method: "users/password/reset",
										data: { token: code, password: data.password },
									});
									setBusy(false);

									if (response.error) return { data, error: response.error };
									const result = (response.data as any)?.data ?? response.data;
									if (result?.status === true || result === true) {
										setCompleted(true);
										clearAuthStorage();
									}
								}
								return { data };
							}}
							onRequest={async () => getForm()}
						/>
					</>
				) : null}

				{!checking ? <AuthDivider /> : null}
				{!checking ? (
					<Button
						title="Back to sign in"
						variant={completed ? "primary" : "secondary"}
						onClick={() => (location.href = "/")}
						sx={completed ? AUTH_PRIMARY_BUTTON_SX : AUTH_SECONDARY_BUTTON_SX}
					/>
				) : null}
			</AuthCard>
		</StartScreenView>
	);
};
